import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, Flower2, XCircle } from "lucide-react";

export default function PaymentResult({ canceled = false }) {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [status, setStatus] = useState(canceled ? "canceled" : "polling");

  useEffect(() => {
    if (canceled) return;
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); return; }
    let n = 0;
    const t = setInterval(async () => {
      n++;
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") { setStatus("paid"); refresh(); clearInterval(t); }
        else if (["failed", "expired"].includes(r.data.status)) { setStatus("failed"); clearInterval(t); }
      } catch { /* keep polling */ }
      if (n > 15) { setStatus("timeout"); clearInterval(t); }
    }, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="payment-result">
      <div className="bg-card border border-border rounded-2xl p-10 max-w-md text-center space-y-4">
        {status === "polling" && (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
            <p className="font-display text-xl font-medium">Confirming your payment…</p>
            <p className="text-sm text-muted-foreground">Just a moment while Stripe tells us it went through.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <Flower2 className="h-8 w-8 text-primary mx-auto" />
            <p className="font-display text-xl font-medium" data-testid="payment-success-title">Welcome to the garden.</p>
            <p className="text-sm text-muted-foreground">Your membership is active. Thank you for helping Bouquet grow.</p>
            <Link to="/" data-testid="payment-success-home" className="inline-block px-6 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold">Back to My Bouquet</Link>
          </>
        )}
        {(status === "canceled" || status === "failed" || status === "error" || status === "timeout") && (
          <>
            <XCircle className="h-8 w-8 text-muted-foreground mx-auto" />
            <p className="font-display text-xl font-medium" data-testid="payment-cancel-title">
              {status === "canceled" ? "No charge made" : "We couldn't confirm that payment"}
            </p>
            <p className="text-sm text-muted-foreground">
              {status === "canceled" ? "You stepped away before paying — nothing was charged." : "If money left your account, it will settle or refund automatically."}
            </p>
            <Link to="/pricing" data-testid="payment-retry-link" className="inline-block px-6 py-2.5 rounded-full border border-border text-sm font-semibold hover:bg-secondary transition-colors">Back to plans</Link>
          </>
        )}
      </div>
    </div>
  );
}
